import { memo } from "react";
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, type Edge, type EdgeProps } from "@xyflow/react";
import { Check, Plus, ShieldCheck, X, Zap } from "lucide-react";
import { cn } from "@ncp/ui";
import type { DiffStatus } from "./nodes";
import type { WfEdge } from "../types";

export interface BuilderEdgeData extends Record<string, unknown> {
  edge: WfEdge;
  diff?: DiffStatus;
  ghost?: boolean;
  sim?: boolean;
  locale: "en" | "ar";
  editable: boolean;
  onInsert?: (edgeId: string, x: number, y: number) => void;
}

function stroke(d: BuilderEdgeData, selected?: boolean) {
  if (d.diff === "removed" || d.ghost) return "var(--color-danger-400)";
  if (d.diff === "added") return "var(--color-success-500)";
  if (d.diff === "modified") return "var(--color-warning-500)";
  if (selected) return "var(--color-aegold-600)";
  if (d.sim) return "var(--color-aegold-500)";
  if (d.edge.outcome === "approved") return "var(--color-success-500)";
  if (d.edge.outcome === "rejected") return "var(--color-danger-400)";
  if (d.edge.automatic) return "var(--color-seablue-400)";
  return "var(--color-sand-400)";
}

function BuilderEdgeView({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected, markerEnd }: EdgeProps<Edge<BuilderEdgeData>>) {
  const d = data!;
  const [path, lx, ly] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 14, offset: 24 });
  const conditional = !!d.edge.guard || d.edge.condition != null;
  const label = d.locale === "ar" && d.edge.labelAr ? d.edge.labelAr : d.edge.label ?? d.edge.trigger;
  const color = stroke(d, selected);
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} interactionWidth={22} style={{ stroke: color, strokeWidth: selected || d.sim ? 2.5 : 1.6, strokeDasharray: conditional || d.ghost || d.diff === "removed" ? "6 4" : undefined }} />
      <EdgeLabelRenderer>
        <div style={{ transform: `translate(-50%, -50%) translate(${lx}px, ${ly}px)` }} className="nodrag nopan group/edge pointer-events-auto absolute flex items-center gap-1">
          <span
            className={cn(
              "inline-flex max-w-[180px] items-center gap-1 truncate rounded-md border bg-white/95 px-1.5 py-0.5 text-[10px] text-sand-800 shadow-sm transition",
              selected ? "border-aegold-400 ring-2 ring-aegold-200" : "border-sand-200",
              d.diff === "added" && "border-success-300 bg-success-50",
              d.diff === "modified" && "border-warning-300 bg-warning-50",
              (d.diff === "removed" || d.ghost) && "border-danger-200 bg-danger-50 line-through opacity-70",
            )}
            dir="auto"
          >
            {d.edge.automatic ? <Zap className="size-3 text-seablue-600" /> : null}
            {d.edge.outcome === "approved" ? <Check className="size-3 text-success-600" /> : d.edge.outcome === "rejected" ? <X className="size-3 text-danger-600" /> : null}
            {conditional ? <ShieldCheck className="size-3 text-aegold-700" /> : null}
            <span className="truncate">{label}</span>
            {d.edge.allowedRoles.length ? <span className="text-sand-500">· {d.edge.allowedRoles.length}</span> : null}
            {(d.edge.actions?.length ?? 0) > 0 ? <span className="inline-flex items-center text-seablue-700"><Zap className="size-2.5" />{d.edge.actions!.length}</span> : null}
          </span>
          {d.editable && !d.ghost && d.onInsert ? (
            <button
              type="button"
              title="Insert a step here"
              onClick={(e) => { e.stopPropagation(); d.onInsert?.(id, e.clientX, e.clientY); }}
              className="grid size-5 place-items-center rounded-full border border-aegold-300 bg-white text-aegold-700 opacity-0 shadow-sm transition hover:bg-aegold-50 group-hover/edge:opacity-100"
            >
              <Plus className="size-3" />
            </button>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export const BuilderEdge = memo(BuilderEdgeView);
